/**
 * The two worked activation reports.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * Two arithmetic faults in the 2.0 version are corrected in the port, in the
 * domain layer rather than here:
 *
 *   - `monthly` held bar-height percentages but was LABELLED as money, and the
 *     twelve values summed to roughly six times the season total. The values are
 *     kept as relative weights and rescaled to the competition total at render
 *     time. See `rescaleMonthly` in src/domain/activation.
 *   - The competition table's "average reach" column was summed as if it were a
 *     total. It is a per-match average (TV reach ÷ matches) and is labelled and
 *     computed as one.
 *
 * Survey respondents are initialised only and no contact data is carried —
 * survey inputs are minimised to what the report actually needs.
 */

import type { DemoActivationReport, DemoStadiumReport } from '../../contracts';

export const DEMO_WIEN: DemoActivationReport = {
  kind: 'front-of-jersey',
  id: 'demo-wien',
  title: 'SK Rapid Wien × Wien Energie',
  sub: 'Front-of-Jersey Deep Dive | Season 2024/25',
  kpi: {
    mediaValue: 12.4,
    mediaValueChange: 18,
    visibilityMin: 1847,
    tvReach: 18.2,
    matches: 34,
  },
  competitions: [
    { name: 'UEFA Champions League', matches: 6, minutes: 412, value: 4.8, reach: 1400000 },
    { name: 'Austrian Bundesliga', matches: 22, minutes: 1124, value: 6.1, reach: 428000 },
    { name: 'ÖFB Cup', matches: 4, minutes: 198, value: 0.98, reach: 312000 },
    { name: 'Friendly Matches', matches: 2, minutes: 113, value: 0.52, reach: 187000 },
  ],
  topMatches: [
    {
      opp: 'Bayern München (A)',
      date: 'Nov 8, 2024',
      comp: 'UCL',
      reach: 2.8,
      value: 1.2,
      min: 88,
    },
    {
      opp: 'Manchester City (H)',
      date: 'Dec 12, 2024',
      comp: 'UCL',
      reach: 2.1,
      value: 0.94,
      min: 82,
    },
    {
      opp: 'Austria Wien (H)',
      date: 'Sep 15, 2024',
      comp: 'Bundesliga',
      reach: 1.8,
      value: 0.82,
      min: 76,
    },
    {
      opp: 'Red Bull Salzburg (A)',
      date: 'Oct 27, 2024',
      comp: 'Bundesliga',
      reach: 1.4,
      value: 0.71,
      min: 71,
    },
    {
      opp: 'Austria Wien (A)',
      date: 'Mar 23, 2025',
      comp: 'Bundesliga',
      reach: 1.4,
      value: 0.69,
      min: 69,
    },
  ],
  // Relative weights only — rescaled to the season total before display.
  monthly: [
    ['Aug', 60],
    ['Sep', 75],
    ['Oct', 85],
    ['Nov', 95],
    ['Dec', 88],
    ['Jan', 70],
    ['Feb', 78],
    ['Mar', 82],
    ['Apr', 72],
    ['May', 65],
  ],
  platforms: [
    { name: 'Facebook', reach: 8.4, impressions: 6.2, engagements: 612, third: ['Shares', '48K'] },
    {
      name: 'Instagram',
      reach: 11.2,
      impressions: 9.8,
      engagements: 924,
      third: ['Stories', '1.4M'],
    },
    {
      name: 'X (Twitter)',
      reach: 5.0,
      impressions: 4.1,
      engagements: 287,
      third: ['Retweets', '31K'],
    },
  ],
  content: [
    {
      type: 'Video',
      title: 'Champions League Qualification Celebration',
      date: 'May 18, 2024',
      impr: 2.8,
      eng: 342,
      platform: 'Instagram',
    },
    {
      type: 'Photo',
      title: 'Vienna Derby Kit Reveal',
      date: 'Sep 12, 2024',
      impr: 1.9,
      eng: 214,
      platform: 'Facebook',
    },
    {
      type: 'Video',
      title: 'Bayern München Match Highlights',
      date: 'Nov 9, 2024',
      impr: 1.7,
      eng: 198,
      platform: 'X (Twitter)',
    },
    {
      type: 'Photo',
      title: 'Player of the Month — Sponsor Feature',
      date: 'Oct 3, 2024',
      impr: 1.4,
      eng: 167,
      platform: 'Instagram',
    },
  ],
  sentiment: [
    ['Positive', 72, 'good'],
    ['Neutral', 21, 'info'],
    ['Negative', 7, 'bad'],
  ],
  engRate: [
    ['Match Highlights', 8.2],
    ['Player Features', 6.7],
    ['Behind the Scenes', 5.4],
    ['Match Day Graphics', 4.8],
  ],
  survey: {
    n: 2847,
    items: [
      ['Brand Recognition', 94, 'of fans correctly identified the front-of-jersey sponsor'],
      ['Positive Brand Association', 78, 'view the sponsor more favourably due to the partnership'],
      ['Purchase Intent', 42, 'more likely to consider the sponsor as their energy provider'],
      ['Sponsorship Fit', 86, 'believe the sponsor is a good fit for the club'],
    ],
  },
  attributes: [
    ['Local & Vienna-focused', 89],
    ['Sustainability & Green Energy', 82],
    ['Community-oriented', 76],
    ['Trustworthy & Reliable', 71],
    ['Innovation & Technology', 64],
    ['Premium Quality', 58],
  ],
  testimonials: [
    {
      ini: 'MS',
      head: "Perfect match for Vienna's club",
      quote:
        'The sponsor being on the shirt just makes sense. They are Vienna, we are Vienna. It feels authentic and I am proud to wear the jersey.',
      by: '— Michael S., Season Ticket Holder',
    },
    {
      ini: 'KL',
      head: 'Sustainability leader supporting our club',
      quote:
        'I switched provider because of their commitment to the club and to green energy. Supporting the sponsor supports the club.',
      by: '— Katharina L., Fan Club Member',
    },
  ],
  recs: [
    [
      'Leverage Champions League success',
      "With European football secured for next season, invest in co-branded content highlighting the sponsor role in the club's international journey. Target: 25% increase in European broadcast visibility.",
      'trendUp',
    ],
    [
      'Expand digital activation',
      'Social media performance exceeded broadcast metrics. Create a dedicated content series featuring player sustainability stories and fan energy challenges.',
      'users',
    ],
    [
      'Convert brand awareness to customer acquisition',
      '94% brand recognition is excellent, but only 42% purchase intent. Launch a fan discount programme to convert awareness into customers. Estimated potential: 8,500 new customer acquisitions.',
      'target',
    ],
    [
      'Premium partnership tier upgrade',
      "Consider expanding from front-of-jersey to a multi-rights package including stadium naming discussions, training kit, and the women's team to maximise market dominance.",
      'award',
    ],
  ],
};

export const DEMO_ALLIANZ: DemoStadiumReport = {
  kind: 'stadium',
  id: 'demo-allianz',
  title: 'SK Rapid Wien × Allianz',
  sub: "Stadium & Women's Team Report 2024/25",
  confidence: 95,
  games: [
    ['Matchday 1', 96],
    ['Matchday 4', 93],
    ['Matchday 8', 97],
    ['Matchday 12', 91],
    ['Matchday 16', 95],
    ['Matchday 20', 92],
    ['Matchday 24', 98],
    ['Matchday 28', 90],
  ],
  womensReach: '3.8M',
  guestSatisfaction: 4.7,
  guestsHosted: 640,
  experience: [
    ['Welcome and arrival', 4.8],
    ['Catering', 4.6],
    ['Seat and view', 4.9],
    ['Matchday programme', 4.4],
    ['Post-match access', 4.5],
  ],
};

export const ACTIVATION_DEMOS = [DEMO_WIEN, DEMO_ALLIANZ];
